import update from "immutability-helper";
import { type ReactNode } from "react";

import { queryClient } from "helpers/queryClient";

import { type BTSelectItem } from "types/apiResponse/apiResponse";

import { getRelatedItemLink } from "utilities/relatedItem/relatedItem";

import { EntityRecordLinkInfo } from "commonComponents/entity/entityRecord/EntityRecord.types";
import { type RelatedItemType } from "commonComponents/entity/relatedItem/RelatedItem.types";

import {
    type AvailableEntitiesResponse,
    type AvailableEntitiesResponseEntitiesItem,
    type EntityRecordResponse,
    type EntityType,
    getEntityRecordsAvailableOnJobGetV2QueryKey,
    getEntityRecordsLinkedEntitiesBatchBySourceExternalIdGetV2QueryKey,
    type GroupedLinkedEntitiesResponse,
    type LinkedEntitiesByEntityEntityRecordsItem,
} from "handlers";

import { getEntityRouteConfig, RELATED_ENTITIES_PREFIX } from "./EntityLinkRoutes.utilities";
import { type EntityStatusRenderer } from "./EntityLinkSelector.types";

/**
 * Props for a related item tag (without router props)
 * Router props (history, location, match) are injected by the router
 */
export interface IEntityLinkTagProps {
    id: number;
    externalId: string;
    itemType: RelatedItemType;
    subject: string;
    canRemove: boolean;
    onRemove: () => void;
    to: string;
    isToFullUrl: boolean;
    status?: ReactNode;
}

/**
 * Converts entity IDs to EntityRecordLinkInfo array
 * Generic version of convertSpecIdsToEntityRecordLinks
 */
export function convertEntityIdsToEntityRecordLinks(
    entityIds: string[],
    sourceExternalId: string,
    sourceEntityType: EntityType,
    targetEntityType: EntityType,
): EntityRecordLinkInfo[] {
    return entityIds.map((entityId) => {
        return new EntityRecordLinkInfo({
            sourceExternalId,
            sourceEntityType,
            targetExternalId: entityId,
            targetEntityType,
        });
    });
}

/**
 * Converts BTSelectItem array to EntityRecordLinkInfo array
 * Only includes selected items in the result
 */
export function convertBTSelectItemsToEntityLinks<T>(
    items: BTSelectItem<T>[],
    sourceExternalId: string,
    sourceEntityType: EntityType,
    targetEntityType: EntityType,
): EntityRecordLinkInfo[] {
    const selectedIds = items.filter((item) => item.selected).map((item) => String(item.value));
    return convertEntityIdsToEntityRecordLinks(
        selectedIds,
        sourceExternalId,
        sourceEntityType,
        targetEntityType,
    );
}

/**
 * Extracts linked entity IDs from EntityRecordLinkInfo array
 * Only returns IDs that exist in the available entities list
 * Generic version of getLinkedSpecificationIds
 */
export function getLinkedEntityIds<T>(
    entityRecordLinks: EntityRecordLinkInfo[],
    availableEntities: BTSelectItem<T>[],
    targetEntityType: EntityType,
): string[] {
    if (!entityRecordLinks || entityRecordLinks.length === 0 || availableEntities.length === 0) {
        return [];
    }

    const linkedIds = entityRecordLinks
        .filter((link) => link.targetEntityType === targetEntityType)
        .map((link) => link.targetExternalId);

    // If we have available entities, filter out any IDs that don't exist
    // This handles the case where an entity was deleted but the link still exists
    const availableIds = new Set(availableEntities.map((entity) => entity.id));
    return linkedIds.filter((id) => availableIds.has(id));
}

/**
 * Creates RelatedItemTag props directly from API entity records
 * Bypasses BTSelectItem conversion for better performance in readonly mode
 */
export function createRelatedItemTagPropsFromEntities(
    entities: EntityRecordResponse[],
    targetEntityType: EntityType,
    jobId: number,
    onRemove: (externalId: string) => void,
    canRemove: boolean = false,
    renderStatus?: EntityStatusRenderer,
): IEntityLinkTagProps[] {
    if (!entities || entities.length === 0) {
        return [];
    }

    const routeConfig = getEntityRouteConfig(targetEntityType);

    return entities.map((entity) => {
        // Use existing utility - entity ID is always >0 for existing entities
        const entityRoute = getRelatedItemLink(
            entity.entityId,
            routeConfig.relatedItemType,
            jobId,
            entity.id,
        );

        // Handle both string and IRouteLocationState return types
        const routePath = typeof entityRoute === "string" ? entityRoute : entityRoute.pathname;

        // Use custom status renderer if provided
        const statusBadge = renderStatus?.(entity);

        return {
            id: entity.entityId,
            externalId: entity.id,
            itemType: routeConfig.relatedItemType,
            subject: entity.title,
            canRemove: canRemove,
            onRemove: () => onRemove(entity.id),
            to: `${RELATED_ENTITIES_PREFIX}${routePath}`,
            isToFullUrl: false,
            status: statusBadge,
        };
    });
}

// ============================================================================
// React Query Cache Utilities
// ============================================================================

/**
 * Updates the batch linked entities cache with the provided entities for a specific type.
 * Callers are responsible for preparing the updated entities array (add, remove, replace, etc).
 */
export function updateLinkedEntitiesCache(
    sourceExternalId: string,
    targetEntityType: EntityType,
    entities: EntityRecordResponse[],
    queryKeyEntityTypes: EntityType[],
): void {
    const batchQueryKey = getEntityRecordsLinkedEntitiesBatchBySourceExternalIdGetV2QueryKey(
        sourceExternalId,
        { targetEntityTypes: queryKeyEntityTypes },
    );
    const batchEntities = entities as LinkedEntitiesByEntityEntityRecordsItem[];

    queryClient.setQueryData(
        batchQueryKey,
        (oldData: { data: GroupedLinkedEntitiesResponse } | undefined) => {
            if (!oldData) {
                return {
                    data: {
                        entitiesByType: [
                            { entityType: targetEntityType, entityRecords: batchEntities },
                        ],
                    },
                };
            }
            const hasType = oldData.data.entitiesByType.some(
                (g) => g.entityType === targetEntityType,
            );
            return update(oldData, {
                data: {
                    entitiesByType: {
                        $set: hasType
                            ? oldData.data.entitiesByType.map((g) =>
                                  g.entityType === targetEntityType
                                      ? { ...g, entityRecords: batchEntities }
                                      : g,
                              )
                            : [
                                  ...oldData.data.entitiesByType,
                                  { entityType: targetEntityType, entityRecords: batchEntities },
                              ],
                    },
                },
            });
        },
    );
}

/**
 * Updates the available entities cache with the provided entities.
 * Callers are responsible for preparing the updated entities array (add, remove, replace, etc).
 */
export function updateAvailableEntitiesCache(
    jobId: number,
    entityType: EntityType,
    entities: EntityRecordResponse[],
): void {
    const queryKey = getEntityRecordsAvailableOnJobGetV2QueryKey({
        jobParam: jobId,
        entityType,
    });

    queryClient.setQueryData(
        queryKey,
        (oldData: { data: AvailableEntitiesResponse } | undefined) => {
            if (!oldData) {
                return { data: { entities: entities as AvailableEntitiesResponseEntitiesItem[] } };
            }
            return update(oldData, {
                data: {
                    entities: { $set: entities as AvailableEntitiesResponseEntitiesItem[] },
                },
            });
        },
    );
}

/**
 * Prepends an entity to the available entities cache.
 *
 * @param jobId - The job ID for which to update the available entities cache
 * @param entityType - The type of entities to update
 * @param entity - The entity to add
 *
 * If no cached data exists, seeds it with the single entity.
 */
export function addToAvailableEntitiesCache(
    jobId: number,
    entityType: EntityType,
    entity: EntityRecordResponse,
): void {
    const queryKey = getEntityRecordsAvailableOnJobGetV2QueryKey({
        jobParam: jobId,
        entityType,
    });

    const cachedData = queryClient.getQueryData<{ data: AvailableEntitiesResponse }>(queryKey);
    const current = cachedData?.data.entities ?? [];
    updateAvailableEntitiesCache(jobId, entityType, [entity, ...current]);
}

/**
 * Removes an item from the available entities cache.
 *
 * @param jobId - The job ID for which to update the available entities cache
 * @param entityType - The type of entities to update
 * @param externalId - The external ID of the entity to remove
 *
 * If no cached data exists, does nothing.
 */
export function removeFromAvailableEntitiesCache(
    jobId: number,
    entityType: EntityType,
    externalId: string,
): void {
    const queryKey = getEntityRecordsAvailableOnJobGetV2QueryKey({
        jobParam: jobId,
        entityType,
    });

    const cachedData = queryClient.getQueryData<{ data: AvailableEntitiesResponse }>(queryKey);
    if (cachedData?.data.entities) {
        const filtered = cachedData.data.entities.filter((entity) => entity.id !== externalId);
        updateAvailableEntitiesCache(jobId, entityType, filtered);
    }
}

/**
 * Updates an item in the available entities cache.
 *
 * @param jobId - The job ID for which to update the available entities cache
 * @param entityType - The type of entities to update
 * @param updatedEntity - The updated entity data
 *
 * If no cached data exists, does nothing.
 */
export function updateItemInAvailableEntitiesCache(
    jobId: number,
    entityType: EntityType,
    updatedEntity: EntityRecordResponse,
): void {
    const queryKey = getEntityRecordsAvailableOnJobGetV2QueryKey({
        jobParam: jobId,
        entityType,
    });

    const cachedData = queryClient.getQueryData<{ data: AvailableEntitiesResponse }>(queryKey);
    if (cachedData?.data.entities) {
        const updated = cachedData.data.entities.map((entity) =>
            entity.id === updatedEntity.id ? updatedEntity : entity,
        );
        updateAvailableEntitiesCache(jobId, entityType, updated);
    }
}
